import { CalendarIcon, LayoutGrid, SlidersHorizontal, Wand2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useState } from 'react';
import { useGenerationContext } from '../../contexts/GenerationContext';
import { CalendarDay } from '../features/calendar/CalendarDay';
import { MobilePostGalleryHeader } from '../features/posts/MobilePostGalleryHeader';
import type { Post, CommunicationChannel } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { getLocaleFromLanguage, capitalizeFirstLetter } from '../../i18n';
import { getMonthDays } from '../../utils/dateUtils';
import { Logger } from '../../utils/logger';

interface MobileBottomBarProps {
  queue: Post[];
  newsletterPosts: Post[];
  onAddToNewsletter: (post: Post) => void;
  onUpdatePost: (post: Post, scheduledDate?: Date) => void;
  onUnschedule?: (postId: string) => void;
  onRemoveFromNewsletter: (post: Post) => void;
  onDeletePost?: (postId: string) => void;
  communicationChannels: CommunicationChannel[];
  onScheduleNewsletter?: (scheduledDate: Date, introduction: string, posts: Post[]) => void;
  allPosts?: Post[];
  logoUrl?: string;
  /** V4-MOBILE-CTRL-STATE — le burger est entièrement piloté par le parent. */
  isBurgerOpen?: boolean;
  onBurgerToggle?: () => void;
  // Props for header
  availableChannels?: CommunicationChannel[];
  selectedChannelIds?: Set<string>;
  onToggleChannel?: (channelId: string) => void;
  showExternal?: boolean;
  showInternal?: boolean;
  onToggleExternal?: () => void;
  onToggleInternal?: () => void;
  // V4-POSTS-CREATED-BY-USER-ID — filtre « mes posts ». MobileBottomBar est le
  // relais entre DashboardPage et MobilePostGalleryHeader : sans ces 3 props le
  // filtre n'atteint jamais le header mobile.
  showOnlyMine?: boolean;
  onToggleOnlyMine?: () => void;
  canFilterMine?: boolean;
  startDate?: string;
  endDate?: string;
  onStartDateChange?: (date: string) => void;
  onEndDateChange?: (date: string) => void;
  // Additional props for header panel
  searchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
  sortType?: 'date' | 'channel' | 'alphabetical';
  sortOrder?: 'asc' | 'desc';
  onSortTypeChange?: (type: 'date' | 'channel' | 'alphabetical') => void;
  onSortOrderChange?: (order: 'asc' | 'desc') => void;
  /** UX backlog : statut de filtre exposé pour MobilePostGalleryHeader (manquait avant). */
  statusFilter?: import('../../utils/Constants').PostStatusFilter;
  onStatusFilterChange?: (status: import('../../utils/Constants').PostStatusFilter) => void;
  onCreatePost?: () => void;
  onGeneratePosts?: () => void;
  onClearGallery?: () => void;
  onGenerateComplete?: (posts: Post[]) => void;
  onCreateComplete?: (post: Post) => void;
}

export function MobileBottomBar({
  queue,
  newsletterPosts,
  onAddToNewsletter,
  onUpdatePost,
  onUnschedule,
  onRemoveFromNewsletter,
  onDeletePost,
  communicationChannels,
  onScheduleNewsletter,
  logoUrl,
  isBurgerOpen = false,
  onBurgerToggle,
  // Props for header
  selectedChannelIds,
  onToggleChannel,
  showExternal,
  showInternal,
  onToggleExternal,
  onToggleInternal,
  showOnlyMine,
  onToggleOnlyMine,
  canFilterMine,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  // Additional props for header panel
  searchQuery,
  onSearchQueryChange,
  sortType,
  sortOrder,
  onSortTypeChange,
  onSortOrderChange,
  statusFilter,
  onStatusFilterChange,
  onClearGallery,
  onGenerateComplete,
  onCreateComplete,
}: MobileBottomBarProps) {
  const [mode, setMode] = useState<'closed' | 'agenda' | 'header'>('closed');
  const [currentDate, setCurrentDate] = useState(new Date());
  const { t, language } = useLanguage();
  const { isGenerating, showGenerationOverlay, setShowGenerationOverlay } = useGenerationContext();
  const locale = getLocaleFromLanguage(language);

  if (!t || !t.posts) {
    Logger.error('❌ MobileBottomBar: early return - missing translations!');
    return null;
  }

  const monthDays = getMonthDays(currentDate.getFullYear(), currentDate.getMonth());

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  return (
    <>
      {/* Bottom bar - 4 floating glassmorphism buttons without container */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-[20000] px-4 pb-4 pt-3"
        style={{ 
          pointerEvents: 'none',
        }}
      >
        <div className="flex items-center justify-around w-full">
          {/* Burger button — opens the MobileBurgerMenu drawer (Espaces, Settings, etc.) */}
          <button
            style={{ pointerEvents: 'auto' }}
            onClick={() => {
              // V4-MOBILE-CTRL-STATE — l'état est entièrement géré par le parent.
              // V4-MOBILE-NAV-1 : close the post-toolbar (header mode) before
              // opening the drawer, sinon les deux overlays se superposent et
              // le test E2E voit le mauvais panneau.
              if (!isBurgerOpen && mode !== 'closed') {
                setMode('closed');
              }
              onBurgerToggle?.();
            }}
            className="transition-all active:scale-95"
            aria-label="Ouvrir le menu"
            data-testid="mobile-burger-trigger"
          >
            <div 
              className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              }}
            >
              {logoUrl ? (
                <img src={logoUrl} alt="Menu" className="w-8 h-8 object-contain rounded-full" />
              ) : (
                <LayoutGrid className="w-6 h-6" style={{ color: 'var(--color-text-primary)' }} />
              )}
            </div>
          </button>

          {/* Header / filters button — opens the post-toolbar slide-down */}
          {/* V4-ONBOARDING-TOUR 2026-06-18 — data-tour="generate" ici car le bouton
              de génération réel est dans le panel MobilePostGalleryHeader (conditionnel).
              Ce bouton est toujours visible → cible stable pour le tour mobile. */}
          <button
            style={{ pointerEvents: 'auto' }}
            data-tour="generate"
            onClick={() => {
              const opening = mode !== 'header';
              setMode(opening ? 'header' : 'closed');
              // V4-MOBILE-NAV-1 : si le drawer burger est ouvert, on le ferme
              // pour éviter que les deux overlays cohabitent.
              if (opening && isBurgerOpen) {
                onBurgerToggle?.();
              }
            }}
            className="transition-all active:scale-95"
            aria-label="Filtres et tri"
            data-testid="mobile-filters-trigger"
          >
            <div 
              className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all"
              style={mode === 'header' ? { 
                backgroundColor: 'var(--color-primary)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid var(--color-primary)',
                boxShadow: '0 8px 32px var(--color-primary-shadow)',
              } : {
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              }}
            >
              <SlidersHorizontal
                className="w-6 h-6"
                style={{ color: mode === 'header' ? 'var(--color-button-text)' : 'var(--color-text-primary)' }}
                stroke={mode === 'header' ? 'var(--color-button-text)' : 'var(--color-text-primary)'}
              />
            </div>
          </button>

          {/* Generate / progress button — visible uniquement si génération en cours — toggle l'overlay */}
          {isGenerating && (
            <button
              style={{ pointerEvents: 'auto' }}
              onClick={() => setShowGenerationOverlay(!showGenerationOverlay)}
              className="transition-all active:scale-95"
              aria-label="Génération en cours"
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
                style={{
                  backgroundColor: 'var(--color-primary)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid var(--color-primary)',
                  boxShadow: '0 8px 32px var(--color-primary-shadow)',
                }}
              >
                <Wand2 className="w-6 h-6 animate-pulse" style={{ color: 'var(--color-button-text)' }} />
              </div>
            </button>
          )}

          {/* Calendar button */}
          <button
            style={{ pointerEvents: 'auto' }}
            onClick={() => setMode(mode === 'agenda' ? 'closed' : 'agenda')}
            className="transition-all active:scale-95"
            // V4-MOBILE-A11Y 2026-05-29 — aria-label pour lecteurs d'écran
            // (FINDING-MOBILE-04). `t.posts.queue` = "File d'attente".
            aria-label={t.posts.queue}
            title={t.posts.queue}
            data-testid="mobile-agenda-trigger"
          >
            <div 
              className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all"
              style={mode === 'agenda' ? { 
                backgroundColor: 'var(--color-primary)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid var(--color-primary)',
                boxShadow: '0 8px 32px var(--color-primary-shadow)',
              } : {
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              }}
            >
              <CalendarIcon 
                className="w-6 h-6" 
                style={{ color: mode === 'agenda' ? 'var(--color-button-text)' : 'var(--color-text-primary)' }}
                stroke={mode === 'agenda' ? 'var(--color-button-text)' : 'var(--color-text-primary)'}
              />
            </div>
          </button>

        </div>
      </div>

      {/* Full screen modals */}
      <AnimatePresence>
        {/* Header panel - slides from top with glassmorphism */}
        {mode === 'header' && (
          <motion.div
            initial={{ y: '-100%' }}
            animate={{ y: 0 }}
            exit={{ y: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed top-4 left-4 right-4 z-[20001]"
          >
            <MobilePostGalleryHeader
              searchQuery={searchQuery || ''}
              onSearchChange={onSearchQueryChange || (() => {})}
              sortType={sortType || 'date'}
              sortOrder={sortOrder || 'desc'}
              onSortTypeChange={onSortTypeChange || (() => {})}
              onSortOrderChange={onSortOrderChange || (() => {})}
              statusFilter={statusFilter}
              onStatusFilterChange={onStatusFilterChange}
              onClearGallery={onClearGallery || (() => {})}
              selectedChannelIds={selectedChannelIds}
              onToggleChannel={onToggleChannel}
              showExternal={showExternal}
              showInternal={showInternal}
              onToggleExternal={onToggleExternal}
              onToggleInternal={onToggleInternal}
              showOnlyMine={showOnlyMine}
              onToggleOnlyMine={onToggleOnlyMine}
              canFilterMine={canFilterMine}
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={onStartDateChange}
              onEndDateChange={onEndDateChange}
              communicationChannels={communicationChannels}
              onGenerateComplete={onGenerateComplete}
              onCreateComplete={onCreateComplete}
            />
          </motion.div>
        )}

        {/* Agenda - slides from right */}
        {mode === 'agenda' && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-[20001] overflow-auto"
            style={{ backgroundColor: 'var(--color-background)' }}
          >
            {/* Header */}
            <div 
              className="sticky top-0 z-10 px-4 py-4 flex items-center justify-between"
              style={{ 
                backgroundColor: 'var(--color-background)',
                borderBottom: `1px solid var(--color-border)`,
              }}
            >
              <h2 
                className="font-semibold text-lg"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {t.posts.queue}
              </h2>
              <button
                onClick={() => setMode('closed')}
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--color-surface)' }}
              >
                <span style={{ color: 'var(--color-text-primary)' }}>✕</span>
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              <div>
                {/* Calendar header */}
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={handlePrevMonth}
                    className="p-2 rounded-full hover:bg-opacity-80"
                    style={{ backgroundColor: 'var(--color-surface)' }}
                  >
                    ‹
                  </button>
                  <h3 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                    {capitalizeFirstLetter(currentDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' }))}
                  </h3>
                  <button
                    onClick={handleNextMonth}
                    className="p-2 rounded-full hover:bg-opacity-80"
                    style={{ backgroundColor: 'var(--color-surface)' }}
                  >
                    ›
                  </button>
                </div>

                {/* Week days */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {t.common.weekDays.map((day, i) => (
                    <div key={i} className="text-center text-xs font-medium py-2" style={{ color: 'var(--color-text-secondary)' }}>
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1">
                  {monthDays.map((day, index) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const compareDay = new Date(day);
                    compareDay.setHours(0, 0, 0, 0);
                    const isToday = compareDay.getTime() === today.getTime();

                    return (
                      <CalendarDay
                        key={index}
                        day={day}
                        isCurrentMonth={day.getMonth() === currentDate.getMonth()}
                        isToday={isToday}
                        posts={queue.filter(post => {
                          if (!post.scheduledDate) return false;
                          const postDate = new Date(post.scheduledDate);
                          return postDate.getFullYear() === day.getFullYear() &&
                            postDate.getMonth() === day.getMonth() &&
                            postDate.getDate() === day.getDate();
                        })}
                        communicationChannels={communicationChannels}
                        onPostClick={(post) => {
                          // Handle post click - could open a modal or navigate
                          Logger.log('Post clicked:', post);
                        }}
                        onUpdatePost={onUpdatePost}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        )}

      </AnimatePresence>
    </>
  );
}